import { useEffect, useState, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import {
  Save, X, Key, CreditCard, AlertCircle, CheckCircle2, Power,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  get_restaurant_setting,
  save_restaurant_setting,
  server_post_data,
} from "../ServiceConnection/serviceconnection";

// ─── Gateway config ────────────────────────────────────────────────────────
const GATEWAYS = [
  {
    id: 1,
    name: "PhonePe",
    description: "UPI, cards & wallet payments via PhonePe",
    field1: { key: "merchant_id", label: "Merchant ID", placeholder: "Enter Merchant ID", icon: CreditCard },
    field2: { key: "api_key", label: "Salt Key (API Key)", placeholder: "Enter Salt Key", secret: true, icon: Key },
  },
  {
    id: 2,
    name: "Razorpay",
    description: "Cards, UPI, netbanking & EMI via Razorpay",
    field1: { key: "merchant_id", label: "Key ID", placeholder: "Enter Key ID", icon: CreditCard },
    field2: { key: "api_key", label: "Key Secret", placeholder: "Enter Key Secret", secret: true, icon: Key },
  },
];

// ─── Default form state ─────────────────────────────────────────────────────
const emptyForm = () => ({ merchant_id: "", api_key: "", uat_prod: "prod" });

export default function PaymentGatewaySetting() {
  const { toast } = useToast();

  // { 1: { merchant_id, api_key }, 2: { merchant_id, api_key } }
  const [forms, setForms] = useState({ 1: emptyForm(), 2: emptyForm() });
  const [originals, setOriginals] = useState({ 1: emptyForm(), 2: emptyForm() });
  const [errors, setErrors] = useState({ 1: {}, 2: {} });
  const [activeGateway, setActiveGateway] = useState(null); // platform_connect id
  const [loading, setLoading] = useState({ fetch: false, save: null, toggle: null });

  // ── Helpers ────────────────────────────────────────────────────────────────
  const hasChanges = (id) =>
    JSON.stringify(forms[id]) !== JSON.stringify(originals[id]);

  const isValid = (id) =>
    forms[id].merchant_id.trim() !== "" && forms[id].api_key.trim() !== "";

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading((p) => ({ ...p, fetch: true }));
    const fd = new FormData();
    fd.append("flag", 1);
    fd.append("want_table_data", 0);
    fd.append("platform_connect_gateway", 1); // triggers fetch of ALL gateways
    try {
      const res = await server_post_data(get_restaurant_setting, fd);
      if (!res.data.error) {
        const list = res.data.message.data_platform_gatway || [];
        const newForms = { 1: emptyForm(), 2: emptyForm() };
        let activeId = null;

        list.forEach((item) => {
          const pid = parseInt(item.platform_connect);
          if (pid === 1 || pid === 2) {
            newForms[pid] = {
              merchant_id: item.marchant_id || "",
              api_key: item.api_key || "",
              uat_prod: item.uat_prod || "prod",
            };
            if (item.active_status === 1) activeId = pid;
          }
        });

        setForms(newForms);
        setOriginals(JSON.parse(JSON.stringify(newForms)));
        setActiveGateway(activeId);
      } else {
        toast({ title: "Error", description: res.data.message, variant: "destructive" });
      }
    } catch {
      toast({ title: "Error", description: "Failed to load payment settings", variant: "destructive" });
    } finally {
      setLoading((p) => ({ ...p, fetch: false }));
    }
  }, [toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Field change ────────────────────────────────────────────────────────────
  const handleChange = (gatewayId, field, value) => {
    setForms((p) => ({ ...p, [gatewayId]: { ...p[gatewayId], [field]: value } }));
    setErrors((p) => {
      const { [field]: _, ...rest } = p[gatewayId];
      return { ...p, [gatewayId]: rest };
    });
  };

  // ── Validate ────────────────────────────────────────────────────────────────
  const validate = (gatewayId) => {
    const errs = {};
    const gw = GATEWAYS.find((g) => g.id === gatewayId);
    if (!forms[gatewayId].merchant_id.trim())
      errs.merchant_id = `${gw.field1.label} is required`;
    if (!forms[gatewayId].api_key.trim())
      errs.api_key = `${gw.field2.label} is required`;
    setErrors((p) => ({ ...p, [gatewayId]: errs }));
    return Object.keys(errs).length === 0;
  };

  // ── Save credentials (from_where=6) ────────────────────────────────────────
  const handleSave = useCallback(async (gatewayId) => {
    if (!validate(gatewayId)) {
      toast({ title: "Validation Error", description: "Fill all required fields", variant: "destructive" });
      return;
    }
    setLoading((p) => ({ ...p, save: gatewayId }));
    const fd = new FormData();
    fd.append("from_where", 6);
    fd.append("platform_connect", gatewayId);
    fd.append("mapping_code", forms[gatewayId].merchant_id);
    fd.append("api_key", forms[gatewayId].api_key);
    fd.append("uat_prod", forms[gatewayId].uat_prod);
    try {
      const res = await server_post_data(save_restaurant_setting, fd);
      if (res.data.error) {
        toast({ title: "Error", description: res.data.message, variant: "destructive" });
      } else {
        toast({ title: "Saved", description: `${GATEWAYS.find((g) => g.id === gatewayId).name} credentials saved` });
        fetchData();
      }
    } catch {
      toast({ title: "Error", description: "Failed to save credentials", variant: "destructive" });
    } finally {
      setLoading((p) => ({ ...p, save: null }));
    }
  }, [forms, toast, fetchData]);

  // ── Toggle active (from_where=7) ─────────────────────────────────────────
  const handleToggleActive = useCallback(async (gatewayId) => {
    // Can only activate if credentials are saved
    if (!isValid(gatewayId)) {
      toast({ title: "No Credentials", description: "Save credentials first before activating", variant: "destructive" });
      return;
    }
    // If already active, just deactivate (pass 0 as platform_connect to deactivate all)
    const isCurrentlyActive = activeGateway === gatewayId;
    setLoading((p) => ({ ...p, toggle: gatewayId }));
    const fd = new FormData();
    fd.append("from_where", 7);
    // pass 0 to deactivate all, or gatewayId to activate
    fd.append("platform_connect", isCurrentlyActive ? 0 : gatewayId);
    try {
      const res = await server_post_data(save_restaurant_setting, fd);
      if (res.data.error) {
        toast({ title: "Error", description: res.data.message, variant: "destructive" });
      } else {
        toast({
          title: isCurrentlyActive ? "Deactivated" : "Activated",
          description: `${GATEWAYS.find((g) => g.id === gatewayId).name} ${isCurrentlyActive ? "deactivated" : "set as active gateway"}`,
        });
        fetchData();
      }
    } catch {
      toast({ title: "Error", description: "Failed to update active gateway", variant: "destructive" });
    } finally {
      setLoading((p) => ({ ...p, toggle: null }));
    }
  }, [activeGateway, forms, toast, fetchData]);

  // ── Render each gateway card ────────────────────────────────────────────────
  const renderGatewayCard = (gw) => {
    const { id, name, description, field1, field2 } = gw;
    const isActive = activeGateway === id;
    const isSaving = loading.save === id;
    const isToggling = loading.toggle === id;
    const credentialsSaved = originals[id].merchant_id !== "" && originals[id].api_key !== "";

    return (
      <Card key={id} className={`relative transition-all ${isActive ? "border-primary ring-1 ring-primary/30" : ""}`}>
        {/* Active badge */}
        {isActive && (
          <div className="absolute top-4 right-4">
            <Badge className="bg-primary/10 text-primary border border-primary/30 gap-1">
              <CheckCircle2 className="w-3 h-3" /> Active
            </Badge>
          </div>
        )}

        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Key className="w-4 h-4 text-primary" />
            {name}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Field 1 */}
          {[field1, field2].map((f) => {
            const Icon = f.icon;
            const err = errors[id]?.[f.key];
            return (
              <div key={f.key} className="space-y-2">
                <Label>
                  {f.label} <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type={f.secret ? "password" : "text"}
                    placeholder={f.placeholder}
                    value={forms[id][f.key]}
                    onChange={(e) => handleChange(id, f.key, e.target.value)}
                    className={`pl-9 ${err ? "border-destructive" : ""}`}
                  />
                </div>
                {err && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> {err}
                  </p>
                )}
              </div>
            );
          })}

          {/* UAT / Production toggle */}
          <div className="space-y-2">
            <Label>Environment</Label>
            <div className="flex rounded-md border overflow-hidden w-fit">
              {["uat", "prod"].map((env) => (
                <button
                  key={env}
                  type="button"
                  onClick={() => handleChange(id, "uat_prod", env)}
                  className={`px-4 py-1.5 text-sm font-medium transition-colors ${forms[id].uat_prod === env
                      ? env === "prod"
                        ? "bg-primary text-primary-foreground"
                        : "bg-warning text-warning-foreground"
                      : "bg-background text-muted-foreground hover:bg-muted"
                    }`}
                >
                  {env === "uat" ? "UAT / Sandbox" : "Production"}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {forms[id].uat_prod === "uat"
                ? "⚠️ Sandbox mode — transactions will not be real"
                : "✅ Production mode — live transactions enabled"}
            </p>
          </div>

          {/* Unsaved changes warning */}
          {hasChanges(id) && (
            <Alert className="border-warning/30 bg-warning/5">
              <AlertCircle className="h-4 w-4 text-warning" />
              <AlertDescription className="text-warning text-sm">
                Unsaved changes — save credentials before activating.
              </AlertDescription>
            </Alert>
          )}

          {/* Action row */}
          <div className="flex items-center gap-2 pt-2 border-t">
            {/* Cancel */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setForms((p) => ({ ...p, [id]: { ...originals[id] } }));
                setErrors((p) => ({ ...p, [id]: {} }));
              }}
              disabled={!hasChanges(id) || isSaving}
              className="gap-1"
            >
              <X className="w-3 h-3" /> Cancel
            </Button>

            {/* Save credentials */}
            <Button
              size="sm"
              onClick={() => handleSave(id)}
              disabled={!hasChanges(id) || isSaving}
              className="gap-1 gradient-primary text-primary-foreground hover:opacity-90"
            >
              {isSaving ? "Saving…" : <><Save className="w-3 h-3" /> Save</>}
            </Button>

            {/* Activate / Deactivate — pushed to right */}
            <Button
              size="sm"
              variant={isActive ? "destructive" : "outline"}
              onClick={() => handleToggleActive(id)}
              disabled={isToggling || !credentialsSaved || hasChanges(id)}
              className="ml-auto gap-1"
              title={!credentialsSaved ? "Save credentials first" : ""}
            >
              <Power className="w-3 h-3" />
              {isToggling ? "…" : isActive ? "Deactivate" : "Set Active"}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">Payment Gateways</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Configure credentials for each gateway. Only one can be active at a time.
        </p>
      </div>

      {/* Active gateway banner */}
      {activeGateway ? (
        <Alert className="border-success/40 bg-success/10">
          <CheckCircle2 className="h-4 w-4 text-success" />
          <AlertDescription className="text-success">
            <strong>{GATEWAYS.find((g) => g.id === activeGateway)?.name}</strong> is currently the active payment gateway.
          </AlertDescription>
        </Alert>
      ) : (
        <Alert className="border-warning/40 bg-warning/10">
          <AlertCircle className="h-4 w-4 text-warning" />
          <AlertDescription className="text-warning">
            No payment gateway is active. Set one to enable payment processing.
          </AlertDescription>
        </Alert>
      )}

      {/* Gateway cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {GATEWAYS.map(renderGatewayCard)}
      </div>
    </div>
  );
}